// Central route registry — the ONLY place routes are declared. Feature agents
// implement pages inside src/features/<area>/ and never touch this file, ws.ts,
// api.ts, or the shell.

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { detectRegion } from './lib/regionDetect';
import { ConciergeWidget } from './concierge/ConciergeWidget';
import { config } from './config';
import { RequireAuth } from './auth/RequireAuth';
import { AppShell } from './components/AppShell';
import { ForceUpgradeOverlay } from './components/ForceUpgradeOverlay';
import LoginPage from './features/login/LoginPage';
import RegisterPage from './features/login/RegisterPage';
import ForgotPasswordPage from './features/login/ForgotPasswordPage';
import ResetPasswordPage from './features/login/ResetPasswordPage';
import VerifyEmailPage from './features/login/VerifyEmailPage';
import DevicesPage from './features/devices/DevicesPage';
import DeviceDetailPage from './features/devices/DeviceDetailPage';
import DeviceLivePage from './features/devices/DeviceLivePage';
import { PanelCalendarPage } from './features/devices/PanelCalendarPage';
import LiveGridPage from './features/live/LiveGridPage';
import MemoriesPage from './features/events/MemoriesPage';
import PlaybackPage from './features/playback/PlaybackPage';
import MultiPlaybackPage from './features/playback/MultiPlaybackPage';
import EventCenterPage from './features/vms/EventCenterPage';
import DeviceOverviewPage from './features/vms/DeviceOverviewPage';
import ExportPage from './features/vms/ExportPage';
import ActivityPage from './features/vms/ActivityPage';
import VerifyPage from './features/vms/VerifyPage';
import PanelConfirmLandingPage from './features/confirm/PanelConfirmLandingPage';
import { EventTimelineRoute, CloudEventTimelineRoute, ContinuousTimelineRoute } from './components/recPlayback';
import SharingPage from './features/sharing/SharingPage';
import AcceptInvitePage from './features/sharing/AcceptInvitePage';
import AccountPage from './features/account/AccountPage';
import GalleryPage from './features/gallery/GalleryPage';
import SessionsPage from './features/account/SessionsPage';
import SubscriptionPage from './features/account/SubscriptionPage';
import MembershipPage from './features/account/MembershipPage';
import HomeOverviewPage from './features/home/HomeOverviewPage';
import SupportPage from './features/support/SupportPage';

const ROUTER_FUTURE = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
} as const;

// Resolve the region once, pre-auth, with zero user input (spec 11 §2/§59): IP
// hint via /v3/geo, OS/locale fallback. Runs only while anonymous — never while
// authenticated, where the account's region (JWT claim) is authoritative and
// re-detection could switch the API host mid-session.
function RegionBootstrap() {
  const { status } = useAuth();
  const ran = useRef(false);
  useEffect(() => {
    if (status === 'anonymous' && !ran.current) {
      ran.current = true;
      const controller = new AbortController();
      void detectRegion(controller.signal);
      // On login/logout the effect re-runs and this cleanup aborts any in-flight
      // detection, so it can't persist a region after the session is established.
      return () => controller.abort();
    }
  }, [status]);
  return null;
}

export default function App() {
  const { t } = useTranslation();
  return (
    <AuthProvider>
      <RegionBootstrap />
      <BrowserRouter future={ROUTER_FUTURE}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          {/* 裝置分享邀請落地頁（公開；APP#131 / spec 18 §5）。未登入時提示登入/註冊。 */}
          <Route path="/shares/:inviteId" element={<AcceptInvitePage />} />
          {/* ls-loop MV.5：證據匯出驗證（公開、不用登入；檔案不上傳） */}
          <Route path="/vms/verify" element={<VerifyPage />} />
          {/* 面板「請用手機確認」QR（docs/panel/10 §2.3）：沒裝 App 時瀏覽器開到這裡，只說明「請用親疼 App 掃描」、不確認（M4）；公開 */}
          <Route path="/c" element={<PanelConfirmLandingPage />} />
          <Route path="/c/:code" element={<PanelConfirmLandingPage />} />
          <Route element={<RequireAuth />}>
            <Route element={<AppShell />}>
              {/* 啟動落點 = 攝影機（業主 2026-09-08，spec 16 §3.1）；總覽保留在 /home 當第一格 tab */}
              <Route index element={<Navigate to="/devices" replace />} />
              <Route path="/home" element={<HomeOverviewPage />} />
              <Route path="/devices" element={<DevicesPage />} />
              <Route path="/devices/:id" element={<DeviceDetailPage />} />
              <Route path="/devices/:id/live" element={<DeviceLivePage />} />
              <Route path="/devices/:id/calendar" element={<PanelCalendarPage />} />
              {/* `/live` 中繼頁已刪（APP#145：Live 非 Tab，多機分割自「攝影機」頁進）；舊連結導回攝影機頁 */}
              <Route path="/live" element={<Navigate to="/devices" replace />} />
              <Route path="/live/grid" element={<LiveGridPage />} />
              {/* 雲端回放 = cloud DDB events + −19/+24 S3 m3u8 playback（只列錄在雲端的事件）. */}
              {/* 「事件」分頁 = 事件中心（帳號全部攝影機、不分錄在哪）；原本這裡是只列雲端錄影的時間軸，大部分事件被濾掉（2026-10-11 John 定 A 案）。
                  全部攝影機的雲端回放移到 /cloud-playback；單台仍是 /events/:deviceId。 */}
              <Route path="/events" element={<EventCenterPage />} />
              <Route path="/cloud-playback" element={<CloudEventTimelineRoute />} />
              <Route path="/events/:deviceId" element={<CloudEventTimelineRoute />} />
              <Route path="/memories" element={<MemoriesPage />} />
              {/* 多機同步回放（共用時鐘 scrubber，APP#43）— 註冊在 :deviceId 之前，避免 "multi" 被當作 deviceId */}
              <Route path="/playback/multi" element={<MultiPlaybackPage />} />
              <Route path="/vms/events" element={<Navigate to="/events" replace />} />
              <Route path="/vms/devices" element={<DeviceOverviewPage />} />
              <Route path="/vms/export" element={<ExportPage />} />
              <Route path="/vms/activity" element={<ActivityPage />} />
              <Route path="/playback/:deviceId" element={<PlaybackPage />} />
              {/* SD 事件時間軸 (sdcard markers + device-VOD playback) + continuous horizontal axis */}
              <Route path="/rec-timeline" element={<EventTimelineRoute />} />
              <Route path="/rec-timeline/:deviceId" element={<EventTimelineRoute />} />
              <Route path="/rec-continuous/:deviceId" element={<ContinuousTimelineRoute />} />
              <Route path="/sharing/:deviceId" element={<SharingPage />} />
              {/* 本地相簿(APP#42):帳號層總覽 + 裝置層,純本地零後端 */}
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/gallery/:deviceId" element={<GalleryPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/account/sessions" element={<SessionsPage />} />
              <Route path="/account/subscription" element={<MembershipPage />} />
              <Route path="/account/subscription/:deviceId" element={<SubscriptionPage />} />
              <Route path="/account/support" element={<SupportPage />} />
              <Route path="*" element={<Navigate to="/devices" replace />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
      {/*
        業主裁決 D69(apps/home/specs/ia.md §5.1,2026-07-30)否決全域浮動客服 FAB:
        Material 對 FAB 的定義是「triggers the primary action」,客服在任何畫面都不是
        主要動作;且 WCAG 2.2 SC 2.4.11(Focus Not Obscured)不容許浮層完全遮蔽取得
        焦點的元件。⇒ `variant="headless"` = 根層不畫任何入口鈕,widget 只保留跨頁的
        對話面板宿主角色,由「帳號 → 說明中心」主入口與各頁情境入口以 openConcierge()
        叫開(與 iOS ConciergePresenter 同語意)。負向斷言見 App.d69.test.tsx。
      */}
      <ConciergeWidget variant="headless" apiBase={config.apiBase} clientId={config.clientId} clientKey={config.clientKey} clientVersion={config.clientVersion} title={t('concierge.appTitle')} />
      <ForceUpgradeOverlay />
    </AuthProvider>
  );
}
